import { useEffect, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import {
  TEMPERATURA_RELACIONAMENTO,
  TEMPERATURA_INFO,
  ESTAGIO_RELACIONAMENTO,
  ESTAGIO_RELACIONAMENTO_LABELS,
  CANAIS_RELACIONAMENTO,
  CANAIS_LABELS,
  PONTAS_STATUS,
  PONTAS_STATUS_LABELS,
  DECISOES_OVERRIDE,
  DECISOES_OVERRIDE_LABELS,
  PontaSolta,
} from '@/pages/painel/lib/inteligencia';
import { normalizarTelefoneBr } from '@/pages/painel/lib/telefone';
import { formatDate } from '@/pages/painel/lib/comercial';

interface Contato360 {
  contato: any;
  empresa_nome: string | null;
  aliases: { id: number; alias: string }[];
  pontas: PontaSolta[];
}

interface Override {
  decisao_manual: string;
  motivo_decisao_manual: string | null;
}

function waLink(phone: string | null | undefined): string | null {
  const d = normalizarTelefoneBr(phone);
  if (!d || d.length < 10) return null;
  return `https://wa.me/55${d}`;
}

function igLink(handle: string | null | undefined): string | null {
  if (!handle) return null;
  let h = String(handle).trim();
  if (!h) return null;
  if (h.startsWith('http')) return h;
  h = h.replace(/^@/, '');
  return `https://instagram.com/${h}`;
}

export default function ContatoDetail() {
  const { password } = useOutletContext<{ password: string }>();
  const { id } = useParams<{ id: string }>();
  const contatoId = Number(id);

  const [data, setData] = useState<Contato360 | null>(null);
  const [override, setOverride] = useState<Override | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  // Relacionamento (edição)
  const [temperatura, setTemperatura] = useState('');
  const [estagio, setEstagio] = useState('');
  const [ultimoCanal, setUltimoCanal] = useState('');
  const [ultimaSituacao, setUltimaSituacao] = useState('');
  const [pendencia, setPendencia] = useState('');
  const [proximaAcao, setProximaAcao] = useState('');
  const [proximaAcaoEm, setProximaAcaoEm] = useState('');
  const [prioridade, setPrioridade] = useState('');
  const [responsavel, setResponsavel] = useState('');
  const [saving, setSaving] = useState(false);

  // Aliases
  const [novoAlias, setNovoAlias] = useState('');
  const [addingAlias, setAddingAlias] = useState(false);

  // Pontas soltas
  const [novaPonta, setNovaPonta] = useState('');
  const [novaOrigem, setNovaOrigem] = useState('whatsapp_normal');
  const [addingPonta, setAddingPonta] = useState(false);

  // Override
  const [decisao, setDecisao] = useState('');
  const [motivoOverride, setMotivoOverride] = useState('');
  const [savingOverride, setSavingOverride] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [res, ovRes] = await Promise.all([
        supabase.functions.invoke('relacionamento-admin', { body: { action: 'contato_360', password, contato_id: contatoId } }),
        supabase.functions.invoke('inteligencia-admin', { body: { action: 'override_listar', password, entidade_tipo: 'contato' } }),
      ]);
      const parsed = res.data as Contato360 & { error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setData(parsed);
      const c = parsed.contato;
      setTemperatura(c.temperatura ?? '');
      setEstagio(c.estagio_relacionamento ?? '');
      setUltimoCanal(c.ultimo_canal ?? '');
      setUltimaSituacao(c.ultima_situacao ?? '');
      setPendencia(c.pendencia ?? '');
      setProximaAcao(c.proxima_acao ?? '');
      setProximaAcaoEm(c.proxima_acao_em ?? '');
      setPrioridade(c.prioridade ?? '');
      setResponsavel(c.responsavel ?? '');

      const ovParsed = ovRes.data as { overrides?: Override[]; error?: string };
      const ov = (ovParsed?.overrides ?? []).find((o: any) => Number(o.entidade_id) === contatoId) ?? null;
      setOverride(ov);
      if (ov) { setDecisao(ov.decisao_manual); setMotivoOverride(ov.motivo_decisao_manual ?? ''); }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar contato.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (contatoId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contatoId, password]);

  const flash = (m: string) => { setMsg(m); setTimeout(() => setMsg(null), 3000); };

  const salvarRelacionamento = async () => {
    setSaving(true);
    try {
      const { data, error: e } = await supabase.functions.invoke('relacionamento-admin', {
        body: {
          action: 'relacionamento_salvar', password, entidade_tipo: 'contato', entidade_id: contatoId,
          temperatura: temperatura || null, estagio_relacionamento: estagio || null, ultimo_canal: ultimoCanal || null,
          ultima_situacao: ultimaSituacao, pendencia, proxima_acao: proximaAcao, proxima_acao_em: proximaAcaoEm || null,
          prioridade, responsavel,
        },
      });
      if (e) throw new Error(await extractErrorMessage(e));
      if (data?.error) throw new Error(data.error);
      flash('Relacionamento atualizado.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar.');
    } finally {
      setSaving(false);
    }
  };

  const acaoRapida = async (patch: any) => {
    setSaving(true);
    try {
      const { data, error: e } = await supabase.functions.invoke('relacionamento-admin', {
        body: { action: 'relacionamento_salvar', password, entidade_tipo: 'contato', entidade_id: contatoId, ...patch },
      });
      if (e) throw new Error(await extractErrorMessage(e));
      if (data?.error) throw new Error(data.error);
      await load();
    } catch {
      // silencioso
    } finally {
      setSaving(false);
    }
  };

  const addAlias = async () => {
    if (!novoAlias.trim()) return;
    setAddingAlias(true);
    try {
      await supabase.functions.invoke('relacionamento-admin', { body: { action: 'alias_salvar', password, contato_id: contatoId, alias: novoAlias.trim() } });
      setNovoAlias('');
      await load();
    } catch { /* silencioso */ } finally { setAddingAlias(false); }
  };

  const removerAlias = async (aliasId: number) => {
    try {
      await supabase.functions.invoke('relacionamento-admin', { body: { action: 'alias_excluir', password, id: aliasId } });
      await load();
    } catch { /* silencioso */ }
  };

  const addPonta = async () => {
    if (!novaPonta.trim()) return;
    setAddingPonta(true);
    try {
      await supabase.functions.invoke('relacionamento-admin', {
        body: { action: 'ponta_salvar', password, contato_id: contatoId, descricao: novaPonta.trim(), origem: novaOrigem, status: 'pendente' },
      });
      setNovaPonta('');
      await load();
    } catch { /* silencioso */ } finally { setAddingPonta(false); }
  };

  const mudarPontaStatus = async (pontaId: number, status: string) => {
    try {
      await supabase.functions.invoke('relacionamento-admin', { body: { action: 'ponta_status', password, id: pontaId, status } });
      await load();
    } catch { /* silencioso */ }
  };

  const salvarOverride = async () => {
    if (!decisao) return;
    setSavingOverride(true);
    try {
      await supabase.functions.invoke('inteligencia-admin', {
        body: { action: 'override_salvar', password, entidade_tipo: 'contato', entidade_id: contatoId, decisao_manual: decisao, motivo_decisao_manual: motivoOverride },
      });
      await load();
    } catch { /* silencioso */ } finally { setSavingOverride(false); }
  };

  const removerOverride = async () => {
    setSavingOverride(true);
    try {
      await supabase.functions.invoke('inteligencia-admin', { body: { action: 'override_remover', password, entidade_tipo: 'contato', entidade_id: contatoId } });
      setDecisao('');
      setMotivoOverride('');
      await load();
    } catch { /* silencioso */ } finally { setSavingOverride(false); }
  };

  if (loading && !data) {
    return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" /></div>;
  }
  if (error && !data) {
    return (
      <div className="space-y-4">
        <Link to="/painel/clientes" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 cursor-pointer whitespace-nowrap"><i className="ri-arrow-left-line"></i> Voltar</Link>
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
      </div>
    );
  }
  if (!data) return null;

  const { contato: c, empresa_nome, aliases, pontas } = data;
  const tempInfo = TEMPERATURA_INFO[c.temperatura];
  const wa = waLink(c.whatsapp || c.telefone);
  const ig = igLink(c.instagram);

  const canais = [
    { key: 'whatsapp_normal', label: 'WhatsApp Normal', icon: 'ri-whatsapp-line', ativo: !!(c.whatsapp || c.telefone), href: wa },
    { key: 'whatsapp_business', label: 'WhatsApp Business', icon: 'ri-whatsapp-line', ativo: c.whatsapp_origem === 'whatsapp_business' || c.whatsapp_origem === 'ambos', href: wa },
    { key: 'instagram', label: 'Instagram', icon: 'ri-instagram-line', ativo: !!c.instagram, href: ig },
    { key: 'linkedin', label: 'LinkedIn', icon: 'ri-linkedin-box-line', ativo: !!c.linkedin, href: c.linkedin ? (c.linkedin.startsWith('http') ? c.linkedin : `https://linkedin.com/in/${c.linkedin}`) : null },
    { key: 'email', label: 'Email', icon: 'ri-mail-line', ativo: !!c.email, href: c.email ? `mailto:${c.email}` : null },
    { key: 'telefone', label: 'Telefone', icon: 'ri-phone-line', ativo: !!(c.telefone || c.whatsapp), href: c.telefone ? `tel:${c.telefone}` : null },
  ];

  return (
    <div className="space-y-6">
      <Link to="/painel/clientes" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 cursor-pointer whitespace-nowrap"><i className="ri-arrow-left-line"></i> Voltar para clientes</Link>

      {msg && <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3">{msg}</div>}
      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}

      {/* Cabeçalho */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4 min-w-0">
            <div className="w-14 h-14 flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-2xl flex-shrink-0">
              <i className="ri-user-star-line text-2xl"></i>
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-xl font-bold text-gray-900">{c.nome || 'Sem nome'}</h2>
                {aliases.length > 0 && (
                  <span className="text-sm text-gray-500">“{aliases.map((a) => a.alias).join(', ')}”</span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap mt-1">
                {Array.isArray(c.tipo_relacionamento) && c.tipo_relacionamento.map((t: string) => (
                  <span key={t} className="text-xs font-semibold text-gray-600 bg-gray-100 rounded-full px-2.5 py-1">{t}</span>
                ))}
                {empresa_nome && (
                  <Link to={c.empresa_id ? `/painel/clientes/${c.empresa_id}` : '/painel/clientes'} className="text-xs font-semibold text-[#E8610A] hover:text-[#C4500A] cursor-pointer whitespace-nowrap">{empresa_nome}</Link>
                )}
                {c.cargo && <span className="text-sm text-gray-500">{c.cargo}</span>}
              </div>
              <div className="flex items-center gap-2 flex-wrap mt-1 text-sm text-gray-500">
                {c.cidade && <span>{c.cidade}{c.uf ? `/${c.uf}` : ''}</span>}
                {c.status_comercial && <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">{c.status_comercial}</span>}
              </div>
            </div>
          </div>
          {tempInfo ? (
            <div className={`inline-flex items-center gap-1.5 text-sm font-bold rounded-full px-3 py-1.5 border ${tempInfo.tone}`}>
              <span>{tempInfo.emoji}</span>{tempInfo.label}
            </div>
          ) : (
            <span className="text-xs text-gray-400">Sem temperatura definida</span>
          )}
        </div>
      </div>

      {/* Por que agora? */}
      {(c.pendencia || c.ultima_situacao) && (
        <div className="bg-[#E8610A]/5 border border-[#E8610A]/20 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <i className="ri-lightbulb-flash-line text-[#E8610A]"></i>
            <h3 className="text-sm font-bold text-gray-900">Por que falar agora?</h3>
          </div>
          <ul className="space-y-1 text-sm text-gray-700">
            {c.cidade && <li>• Localização estratégica: {c.cidade}{c.uf ? `-${c.uf}` : ''}.</li>}
            {Array.isArray(c.tipo_relacionamento) && c.tipo_relacionamento.length > 0 && <li>• Perfil: {c.tipo_relacionamento.join(', ')}.</li>}
            {c.estagio_relacionamento && <li>• Estágio: {ESTAGIO_RELACIONAMENTO_LABELS[c.estagio_relacionamento] ?? c.estagio_relacionamento}.</li>}
            {c.ultima_situacao && <li>• {c.ultima_situacao}.</li>}
            {c.pendencia && <li>• Pendência aberta: {c.pendencia}.</li>}
            {c.temperatura && <li>• Relacionamento classificado como {tempInfo?.label?.toLowerCase()}.</li>}
          </ul>
          {c.proxima_acao && <p className="text-sm font-semibold text-[#E8610A] mt-3">Próxima ação: {c.proxima_acao}</p>}
        </div>
      )}

      {/* Ação rápida */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4 flex items-center gap-2 flex-wrap">
        {wa && (
          <a href={wa} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-green-600 hover:bg-green-700 rounded-lg px-3 py-2 cursor-pointer whitespace-nowrap"><i className="ri-whatsapp-line"></i> WhatsApp</a>
        )}
        {ig && (
          <a href={ig} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-pink-600 hover:bg-pink-700 rounded-lg px-3 py-2 cursor-pointer whitespace-nowrap"><i className="ri-instagram-line"></i> Instagram</a>
        )}
        <button onClick={() => acaoRapida({ estagio_relacionamento: 'reuniao_agendada' })} className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-200 hover:border-[#E8610A] rounded-lg px-3 py-2 cursor-pointer whitespace-nowrap"><i className="ri-team-line"></i> Marcar reunião</button>
        <button onClick={() => acaoRapida({ estagio_relacionamento: 'reuniao_adiada' })} className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-200 hover:border-[#E8610A] rounded-lg px-3 py-2 cursor-pointer whitespace-nowrap"><i className="ri-time-line"></i> Adiar</button>
        <button onClick={() => acaoRapida({ ultimo_canal: 'ligacao' })} className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-200 hover:border-[#E8610A] rounded-lg px-3 py-2 cursor-pointer whitespace-nowrap"><i className="ri-phone-line"></i> Registrar ligação</button>
        <button onClick={() => acaoRapida({ ultimo_canal: 'visita' })} className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-200 hover:border-[#E8610A] rounded-lg px-3 py-2 cursor-pointer whitespace-nowrap"><i className="ri-building-2-line"></i> Registrar visita</button>
      </div>

      {/* Relacionamento (editável) */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
          <div className="flex items-center gap-2">
            <i className="ri-heart-3-line text-[#E8610A]"></i>
            <h3 className="text-sm font-bold text-gray-900">Relacionamento</h3>
          </div>
          <button onClick={salvarRelacionamento} disabled={saving} className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#E8610A] hover:bg-[#C4500A] rounded-lg px-4 py-2 cursor-pointer whitespace-nowrap disabled:opacity-60">
            {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>} Salvar
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-[11px] text-gray-500 font-semibold">Temperatura</label>
            <select value={temperatura} onChange={(e) => setTemperatura(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white cursor-pointer focus:outline-none">
              <option value="">— Sem temperatura —</option>
              {TEMPERATURA_RELACIONAMENTO.map((t) => <option key={t.key} value={t.key}>{t.emoji} {t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] text-gray-500 font-semibold">Estágio do relacionamento</label>
            <select value={estagio} onChange={(e) => setEstagio(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white cursor-pointer focus:outline-none">
              <option value="">— Sem estágio —</option>
              {ESTAGIO_RELACIONAMENTO.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] text-gray-500 font-semibold">Último canal</label>
            <select value={ultimoCanal} onChange={(e) => setUltimoCanal(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white cursor-pointer focus:outline-none">
              <option value="">— Sem canal —</option>
              {CANAIS_RELACIONAMENTO.map((ch) => <option key={ch.key} value={ch.key}>{ch.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] text-gray-500 font-semibold">Prioridade</label>
            <select value={prioridade} onChange={(e) => setPrioridade(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white cursor-pointer focus:outline-none">
              <option value="">—</option>
              <option value="alta">Alta</option>
              <option value="media">Média</option>
              <option value="baixa">Baixa</option>
              <option value="reativar">Reativar</option>
              <option value="monitorar">Monitorar</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-[11px] text-gray-500 font-semibold">Última situação</label>
            <input value={ultimaSituacao} onChange={(e) => setUltimaSituacao(e.target.value)} placeholder="Ex.: reunião adiada porque estaria viajando" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[11px] text-gray-500 font-semibold">Pendência</label>
            <input value={pendencia} onChange={(e) => setPendencia(e.target.value)} placeholder="Ex.: definir nova data para encontro" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[11px] text-gray-500 font-semibold">Próxima ação</label>
            <div className="flex flex-col sm:flex-row gap-2">
              <input value={proximaAcao} onChange={(e) => setProximaAcao(e.target.value)} placeholder="Ex.: propor nova data em Recife" className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]" />
              <input type="date" value={proximaAcaoEm} onChange={(e) => setProximaAcaoEm(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]" />
            </div>
          </div>
          <div>
            <label className="text-[11px] text-gray-500 font-semibold">Responsável</label>
            <input value={responsavel} onChange={(e) => setResponsavel(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]" />
          </div>
        </div>

        {c.ultima_interacao && (
          <p className="text-xs text-gray-400 mt-3">Última interação: {formatDate(c.ultima_interacao)}</p>
        )}
      </div>

      {/* Canais */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <h3 className="text-sm font-bold text-gray-900 mb-4">Canais</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {canais.map((ch) => (
            <div key={ch.key} className={`border rounded-xl p-3 ${ch.ativo ? 'border-gray-200 bg-gray-50' : 'border-dashed border-gray-200 bg-white opacity-50'}`}>
              <div className="flex items-center gap-2 mb-1">
                <i className={`${ch.icon} text-[#E8610A]`}></i>
                <span className="text-xs font-semibold text-gray-700">{ch.label}</span>
              </div>
              {ch.ativo ? (
                ch.href ? (
                  <a href={ch.href} target="_blank" rel="noopener noreferrer" className="text-[11px] text-[#E8610A] hover:text-[#C4500A] cursor-pointer whitespace-nowrap">Abrir canal</a>
                ) : (
                  <span className="text-[11px] text-green-600">Identificado</span>
                )
              ) : (
                <span className="text-[11px] text-gray-300">Não identificado</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Aliases */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <h3 className="text-sm font-bold text-gray-900 mb-1">Aliases / apelidos</h3>
        <p className="text-xs text-gray-500 mb-4">A busca encontra o mesmo contato por qualquer apelido — sem criar registros duplicados.</p>
        {aliases.length > 0 ? (
          <div className="flex items-center gap-2 flex-wrap mb-3">
            {aliases.map((a) => (
              <span key={a.id} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#E8610A] bg-[#E8610A]/10 rounded-full pl-3 pr-1.5 py-1">
                {a.alias}
                <button onClick={() => removerAlias(a.id)} className="text-gray-400 hover:text-red-600 cursor-pointer w-4 h-4 flex items-center justify-center"><i className="ri-close-line"></i></button>
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400 mb-3">Nenhum apelido cadastrado.</p>
        )}
        <div className="flex gap-2">
          <input value={novoAlias} onChange={(e) => setNovoAlias(e.target.value)} placeholder="Adicionar apelido (ex.: Selinha)" className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]" />
          <button onClick={addAlias} disabled={addingAlias || !novoAlias.trim()} className="inline-flex items-center gap-1 text-sm font-semibold text-white bg-[#E8610A] hover:bg-[#C4500A] rounded-lg px-4 py-2 cursor-pointer whitespace-nowrap disabled:opacity-60"><i className="ri-add-line"></i> Adicionar</button>
        </div>
      </div>

      {/* Pontas soltas */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <h3 className="text-sm font-bold text-gray-900 mb-1">Pontas soltas</h3>
        <p className="text-xs text-gray-500 mb-4">Promessas e compromissos futuros — "me chama semana que vem", "vamos marcar um café", etc.</p>
        {pontas.length > 0 ? (
          <div className="space-y-2 mb-4">
            {pontas.map((p) => (
              <div key={p.id} className="flex items-start justify-between gap-3 border border-gray-100 rounded-xl p-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-800">{p.descricao}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {p.origem ? `${CANAIS_LABELS[p.origem] ?? p.origem} · ` : ''}
                    {p.data_conversa ? `${formatDate(p.data_conversa)} · ` : ''}
                    {p.janela_tempo ? `${p.janela_tempo} · ` : ''}
                    {p.proxima_acao ?? 'Sem próxima ação'}
                  </p>
                </div>
                <select value={p.status} onChange={(e) => mudarPontaStatus(p.id, e.target.value)} className="text-xs font-semibold border rounded-full px-2 py-1 cursor-pointer bg-white">
                  {PONTAS_STATUS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400 mb-3">Nenhuma ponta solta registrada.</p>
        )}
        <div className="flex flex-col sm:flex-row gap-2">
          <select value={novaOrigem} onChange={(e) => setNovaOrigem(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white cursor-pointer">
            {CANAIS_RELACIONAMENTO.map((ch) => <option key={ch.key} value={ch.key}>{ch.label}</option>)}
          </select>
          <input value={novaPonta} onChange={(e) => setNovaPonta(e.target.value)} placeholder="Ex.: me chama quando estiver em Recife" className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]" />
          <button onClick={addPonta} disabled={addingPonta || !novaPonta.trim()} className="inline-flex items-center gap-1 text-sm font-semibold text-white bg-gray-800 hover:bg-gray-700 rounded-lg px-4 py-2 cursor-pointer whitespace-nowrap disabled:opacity-60"><i className="ri-add-line"></i> Registrar</button>
        </div>
      </div>

      {/* Override manual */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-1"><i className="ri-shield-check-line text-[#E8610A]"></i><h3 className="text-sm font-bold text-gray-900">Override manual</h3></div>
        <p className="text-xs text-gray-500 mb-4">Decisão sua prevalece sobre IA, score e filas. "Não prospectar" nunca é contrariado por score = 100.</p>
        {override && (
          <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
            Override ativo: {DECISOES_OVERRIDE_LABELS[override.decisao_manual] ?? override.decisao_manual}
            {override.motivo_decisao_manual && ` — ${override.motivo_decisao_manual}`}
          </div>
        )}
        <div className="flex flex-col sm:flex-row gap-2">
          <select value={decisao} onChange={(e) => setDecisao(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white cursor-pointer sm:min-w-[200px]">
            <option value="">— Sem override —</option>
            {DECISOES_OVERRIDE.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
          </select>
          <input value={motivoOverride} onChange={(e) => setMotivoOverride(e.target.value)} placeholder="Motivo (opcional)" className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]" />
          <button onClick={salvarOverride} disabled={savingOverride || !decisao} className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#E8610A] hover:bg-[#C4500A] rounded-lg px-4 py-2 cursor-pointer whitespace-nowrap disabled:opacity-60">{savingOverride ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>} Salvar</button>
          {override && <button onClick={removerOverride} disabled={savingOverride} className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-red-600 rounded-lg px-3 py-2 cursor-pointer whitespace-nowrap">Remover</button>}
        </div>
      </div>
    </div>
  );
}